/**
 * Dashboard Active Content — State 3 (provisioned, setup complete)
 *
 * Renders: Pulse Row (hero) + Needs Attention (conditional) +
 * Storefront Banner + Discovery Prompt + Recent Activity + News.
 * No app grid. No "All clear" card. Empty sections are invisible.
 *
 * Merges Chatwoot pulse data (from Control Plane) with ERP pulse data
 * (from Tenant Server) for unified tile display. Chatwoot loads independently
 * so ERP metrics are never blocked by Chatwoot API latency.
 */

import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type ActivityItem } from "@/hooks/useDashboardData";
import { useGetDashboardPulse, type DashboardPulseResponse, type PulseItem, type NeedsAttentionItem } from "@/hooks/useDashboardPulse";
import { useChatwootPulse } from "@/hooks/useChatwootPulse";
import { usePageTranslation } from "@/i18n/useTranslation";
import { dashboardTranslations } from "@/i18n/locales/en/dashboard";
import { dashboardTranslations as thDashboardTranslations } from "@/i18n/locales/th/dashboard";
import { PulseRow } from "./PulseRow";
import { PeriodSelector } from "./PeriodSelector";
import { usePulseSparkline, type PulsePeriod } from "@/hooks/usePulseSparkline";
import { frappeMethod } from "@/lib/erp/utils";
import { usePreferencesStore } from "@/lib/store/preferencesStore";

const ALL_PERIODS: PulsePeriod[] = ["7d", "mtd", "30d", "90d"];
import { QuickActions } from "./QuickActions";
import { DashboardPendingTasks } from "./DashboardPendingTasks";
import { DashboardStorefrontBanner } from "./DashboardStorefrontBanner";
import { DashboardLiveVisitors } from "./DashboardLiveVisitors";
import { DashboardPipelineWidget } from "./DashboardPipelineWidget";
import { MarketplaceSavingsCard } from "./MarketplaceSavingsCard";
import { DiscoveryPrompt } from "./DiscoveryPrompt";
import { DashboardRecentActivity } from "./DashboardRecentActivity";
import { DashboardNews } from "./DashboardNews";
import { RecentOrdersList } from "./RecentOrdersList";
import { ChannelRow } from "./ChannelRow";
import { LineOARow } from "./LineOARow";
import { UsagePlanCard } from "./UsagePlanCard";
import { RecentChatList } from "./RecentChatList";

interface DashboardActiveContentProps {
  storefrontUrl: string | null;
  storefrontLabels: {
    yourStorefront: string;
    openStorefront: string;
    copyUrl: string;
  };
  recentActivity: ActivityItem[];
  recentActivityTitle: string;
  needsAttentionTitle: string;
  newsLabels: {
    news: string;
    newFeature: string;
    siteCanvas: string;
    siteCanvasDescription: string;
    trySiteCanvas: string;
  };
}

export function DashboardActiveContent({
  storefrontUrl,
  storefrontLabels,
  recentActivity,
  recentActivityTitle,
  needsAttentionTitle,
  newsLabels,
}: DashboardActiveContentProps) {
  const t = usePageTranslation({ en: dashboardTranslations, th: thDashboardTranslations });
  const { data, isLoading: isPulseLoading } = useGetDashboardPulse();
  const pulsePeriod = usePreferencesStore((s) => s.pulsePeriod);
  const setPulsePeriod = usePreferencesStore((s) => s.setPulsePeriod);
  const pulse = data as DashboardPulseResponse | undefined;
  const { data: chatwootPulse, isFetched: chatwootFetched } = useChatwootPulse();

  // Merge Chatwoot tile into pulse tiles (Chatwoot gets highest priority — position 0)
  const mergedPulseItems = useMemo((): PulseItem[] => {
    const erpTiles = pulse?.pulse ?? [];
    const openCount = chatwootPulse?.open_count ?? 0;

    if (openCount <= 0) return erpTiles;

    const chatwootTile: PulseItem = {
      id: "open_chatwoot",
      label: t.chatwootOpenConversations ?? "Open Conversations",
      value: openCount,
      delta: null,
      href: "/inbox",
    };

    return [chatwootTile, ...erpTiles].slice(0, 3);
  }, [pulse?.pulse, chatwootPulse?.open_count, t.chatwootOpenConversations]);

  // Sparkline data — lazy-loaded after main pulse tiles are ready
  const {
    data: sparklineData,
    isLoading: isSparklineLoading,
    isError: isSparklineError,
  } = usePulseSparkline(pulsePeriod, !isPulseLoading && mergedPulseItems.length > 0);

  // Prefetch all other periods in background so switching is instant
  const queryClient = useQueryClient();
  const canPrefetch = !isPulseLoading && mergedPulseItems.length > 0;
  useEffect(() => {
    if (!canPrefetch) return;
    for (const p of ALL_PERIODS) {
      if (p !== pulsePeriod) {
        queryClient.prefetchQuery({
          queryKey: ["pulse-sparkline", p],
          queryFn: () => frappeMethod("zaviago_backend.api.dashboard_sparkline.get_pulse_sparkline", { period: p }),
          staleTime: 5 * 60 * 1000,
        });
      }
    }
  }, [canPrefetch, pulsePeriod, queryClient]);

  // Merge Chatwoot needs-attention item
  const mergedNeedsAttention = useMemo((): NeedsAttentionItem[] => {
    const erpItems = pulse?.needs_attention ?? [];
    const unassignedCount = chatwootPulse?.unassigned_count ?? 0;

    if (unassignedCount <= 0) return erpItems;

    const chatwootItem: NeedsAttentionItem = {
      id: "unassigned_chatwoot",
      title: t.chatwootUnassigned ?? "Unassigned conversations",
      count: unassignedCount,
      href: "/inbox",
      priority: "high",
    };

    return [chatwootItem, ...erpItems];
  }, [pulse?.needs_attention, chatwootPulse?.unassigned_count, t.chatwootUnassigned]);

  // Compute effective discovery — suppress chatwoot_from_customers if tenant has Chatwoot
  // Wait for Chatwoot pulse to load before rendering to prevent flash (gotcha 9)
  const effectiveDiscovery = useMemo(() => {
    const d = pulse?.discovery ?? null;
    if (d?.id === "chatwoot_from_customers") {
      if (!chatwootFetched) return null; // Wait for Chatwoot data before deciding
      if (chatwootPulse?.has_chatwoot_account) return null; // Already has Chatwoot — suppress
    }
    return d;
  }, [pulse?.discovery, chatwootPulse?.has_chatwoot_account, chatwootFetched]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8">
      {/* ── Left Column: Metrics & Channels ── */}
      <div className="lg:col-span-7 xl:col-span-8 space-y-6 lg:space-y-10">
        {/* Storefront Banner — top for easy discovery */}
        {storefrontUrl && (
          <DashboardStorefrontBanner
            storefrontUrl={storefrontUrl}
            labels={storefrontLabels}
          />
        )}

        {/* App Grid — mobile only, super-app style (right below storefront link) */}
        <div className="lg:hidden">
          <QuickActions bare />
        </div>

        {/* Live Visitors — who is on the storefront right now (below apps on mobile) */}
        <DashboardLiveVisitors />

        {/* Data pipeline & retargeting readiness — store-wide marketing assets */}
        <DashboardPipelineWidget />

        {/* Needs Attention */}
        <DashboardPendingTasks
          tasks={mergedNeedsAttention}
          title={needsAttentionTitle}
        />

        {/* Recent Orders */}
        <RecentOrdersList />

        {/* Period Selector + Pulse Metrics */}
        <section>
          {!isPulseLoading && mergedPulseItems.length > 0 && !isSparklineError && (
            <PeriodSelector value={pulsePeriod} onChange={setPulsePeriod} />
          )}
          <PulseRow
            items={mergedPulseItems}
            isLoading={isPulseLoading && !pulse?.pulse?.length}
            sparklineData={isSparklineError ? null : sparklineData ?? null}
            isSparklineLoading={isSparklineLoading}
            period={pulsePeriod}
          />
        </section>

        <hr className="border-border/20" />

        {/* Channels */}
        <section>
          <h3 className="text-sm font-semibold text-foreground mb-3">ช่องทางขาย</h3>
          <ChannelRow />
        </section>

        <hr className="border-border/20" />

        {/* LINE OA */}
        <LineOARow />

        {/* Discovery Prompt */}
        <DiscoveryPrompt discovery={effectiveDiscovery} />
      </div>

      {/* ── Right Column: Apps, Plan, Activity ── */}
      <div className="lg:col-span-5 xl:col-span-4 space-y-5 lg:space-y-8">
        {/* Marketplace fees saved — value metric, top of the right column */}
        <MarketplaceSavingsCard />

        {/* App Grid — desktop only (stays in right column) */}
        <div className="hidden lg:block">
          <QuickActions />
        </div>

        {/* Recent Chat (Chatwoot conversations) */}
        <RecentChatList />

        {/* Usage & Plan */}
        <UsagePlanCard />

        {/* Recent Activity */}
        <DashboardRecentActivity activities={recentActivity} title={recentActivityTitle} />

        {/* News */}
        <DashboardNews labels={newsLabels} variant="compact" />
      </div>
    </div>
  );
}
